import {
  confirmIndustrialCostPurchaseOrdersImport,
  previewIndustrialCostPurchaseOrdersImport,
} from '../services/industrialCostService';
import type {
  PurchaseOrderImportConfirmResponse,
  PurchaseOrderImportPreviewResponse,
} from '../types/industrialCost';

export type PurchaseOrdersImportFlowStatus =
  | 'idle'
  | 'loading'
  | 'needs_mapping'
  | 'needs_review'
  | 'ready'
  | 'confirming'
  | 'imported'
  | 'imported_with_issues'
  | 'error';

export type PurchaseOrdersImportFlowIssue = {
  kind:
    | 'missing_required_fields'
    | 'row_errors'
    | 'unresolved_materials'
    | 'missing_delivery_dates'
    | 'warnings';
  severity: 'blocking' | 'non_blocking';
  count: number;
  message: string;
};

export type PurchaseOrdersImportFlowSummary = {
  canContinue: boolean;
  blockingIssues: PurchaseOrdersImportFlowIssue[];
  nonBlockingIssues: PurchaseOrdersImportFlowIssue[];
  allIssues: PurchaseOrdersImportFlowIssue[];
  missingRequiredFieldsCount: number;
  rowErrorsCount: number;
  unresolvedMaterialsCount: number;
  missingDeliveryDatesCount: number;
  warningsCount: number;
};

export type PurchaseOrdersImportFlowState = {
  file: File | null;
  preview: PurchaseOrderImportPreviewResponse | null;
  confirmResult: PurchaseOrderImportConfirmResponse | null;
  mapping: Record<string, string | null>;
  normalization: {
    sheet_name?: string | null;
    header_row_index?: number | null;
  } | null;
  status: PurchaseOrdersImportFlowStatus;
  error: string | null;
  resultMessage: string | null;
};

export function createPurchaseOrdersImportFlowState(
  file: File | null = null,
): PurchaseOrdersImportFlowState {
  return {
    file,
    preview: null,
    confirmResult: null,
    mapping: {},
    normalization: null,
    status: 'idle',
    error: null,
    resultMessage: null,
  };
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };

    if (maybeError.response?.data?.detail) {
      return maybeError.response.data.detail;
    }

    if (maybeError.message) {
      return maybeError.message;
    }
  }

  return fallback;
}

export function derivePurchaseOrdersImportFlowStatus(
  preview: PurchaseOrderImportPreviewResponse | null,
): PurchaseOrdersImportFlowStatus {
  if (!preview) {
    return 'idle';
  }

  if (preview.missing_required_fields.length > 0) {
    return 'needs_mapping';
  }

  if (preview.row_errors.length > 0) {
    return 'needs_review';
  }

  return 'ready';
}

export async function runPurchaseOrdersImportPreview(
  state: PurchaseOrdersImportFlowState,
  overrides?: {
    file?: File | null;
    mapping?: Record<string, string | null>;
    normalization?: {
      sheet_name?: string | null;
      header_row_index?: number | null;
    } | null;
  },
): Promise<PurchaseOrdersImportFlowState> {
  const file = overrides?.file ?? state.file;
  const mapping = overrides?.mapping ?? state.mapping;
  const normalization =
    overrides?.normalization ?? state.normalization;

  if (!file) {
    return {
      ...state,
      file: null,
      preview: null,
      confirmResult: null,
      mapping,
      normalization: null,
      status: 'idle',
      error: null,
      resultMessage: null,
    };
  }

  try {
    const preview =
      await previewIndustrialCostPurchaseOrdersImport(
        file,
        mapping,
        normalization ?? undefined,
      );

    return {
      ...state,
      file,
      preview,
      confirmResult: null,
      mapping: preview.auto_mapping ?? mapping,
      normalization,
      status: derivePurchaseOrdersImportFlowStatus(preview),
      error: null,
      resultMessage: null,
    };
  } catch (error) {
    return {
      ...state,
      file,
      preview: null,
      confirmResult: null,
      mapping,
      normalization,
      status: 'error',
      error: getErrorMessage(
        error,
        'No se pudo leer el archivo de pedidos de compra.',
      ),
      resultMessage: null,
    };
  }
}

export function getPurchaseOrdersImportSummary(
  state: PurchaseOrdersImportFlowState,
): PurchaseOrdersImportFlowSummary {
  const preview = state.preview;

  if (!preview) {
    return {
      canContinue: false,
      blockingIssues: [],
      nonBlockingIssues: [],
      allIssues: [],
      missingRequiredFieldsCount: 0,
      rowErrorsCount: 0,
      unresolvedMaterialsCount: 0,
      missingDeliveryDatesCount: 0,
      warningsCount: 0,
    };
  }

  const missingRequiredFieldsCount =
    preview.missing_required_fields.length;
  const rowErrorsCount = preview.row_errors.length;
  const unresolvedMaterialsCount =
    preview.coverage_summary.unresolved_material_rows;
  const missingDeliveryDatesCount =
    preview.coverage_summary.rows_without_delivery_date;
  const warningsCount = preview.row_warnings.length;

  const blockingIssues: PurchaseOrdersImportFlowIssue[] = [];
  const nonBlockingIssues: PurchaseOrdersImportFlowIssue[] = [];

  if (missingRequiredFieldsCount > 0) {
    blockingIssues.push({
      kind: 'missing_required_fields',
      severity: 'blocking',
      count: missingRequiredFieldsCount,
      message:
        `Faltan ${missingRequiredFieldsCount} campo(s) ` +
        'obligatorio(s) por mapear.',
    });
  }

  if (rowErrorsCount > 0) {
    blockingIssues.push({
      kind: 'row_errors',
      severity: 'blocking',
      count: rowErrorsCount,
      message:
        `Hay ${rowErrorsCount} línea(s) incompatibles ` +
        'con la publicación del snapshot de compras.',
    });
  }

  if (unresolvedMaterialsCount > 0) {
    nonBlockingIssues.push({
      kind: 'unresolved_materials',
      severity: 'non_blocking',
      count: unresolvedMaterialsCount,
      message:
        `Hay ${unresolvedMaterialsCount} línea(s) ` +
        'sin material resuelto.',
    });
  }

  if (missingDeliveryDatesCount > 0) {
    nonBlockingIssues.push({
      kind: 'missing_delivery_dates',
      severity: 'non_blocking',
      count: missingDeliveryDatesCount,
      message:
        `Hay ${missingDeliveryDatesCount} línea(s) ` +
        'sin fecha de entrega.',
    });
  }

  if (warningsCount > 0) {
    nonBlockingIssues.push({
      kind: 'warnings',
      severity: 'non_blocking',
      count: warningsCount,
      message:
        `Se han detectado ${warningsCount} advertencia(s) ` +
        'en el archivo.',
    });
  }

  return {
    canContinue:
      missingRequiredFieldsCount === 0 &&
      rowErrorsCount === 0,
    blockingIssues,
    nonBlockingIssues,
    allIssues: [
      ...blockingIssues,
      ...nonBlockingIssues,
    ],
    missingRequiredFieldsCount,
    rowErrorsCount,
    unresolvedMaterialsCount,
    missingDeliveryDatesCount,
    warningsCount,
  };
}

export function canContinuePurchaseOrdersImport(
  state: PurchaseOrdersImportFlowState,
): boolean {
  return getPurchaseOrdersImportSummary(state).canContinue;
}

export async function confirmPurchaseOrdersImport(
  state: PurchaseOrdersImportFlowState,
): Promise<PurchaseOrdersImportFlowState> {
  if (!state.file || !state.preview) {
    return {
      ...state,
      status: 'error',
      error: 'No hay un preview válido de pedidos de compra para importar.',
      resultMessage: null,
    };
  }

  if (!canContinuePurchaseOrdersImport(state)) {
    return {
      ...state,
      status: 'error',
      error:
        'El archivo de pedidos de compra todavía contiene ' +
        'incidencias bloqueantes.',
      resultMessage: null,
    };
  }

  try {
    const confirmResult =
      await confirmIndustrialCostPurchaseOrdersImport(
        state.file,
        state.mapping,
        state.normalization ?? undefined,
      );

    const hasIssues =
      confirmResult.rows_unresolved_material > 0 ||
      confirmResult.rows_without_delivery_date > 0 ||
      confirmResult.row_errors.length > 0;

    return {
      ...state,
      confirmResult,
      status: hasIssues
        ? 'imported_with_issues'
        : 'imported',
      error: null,
      resultMessage:
        `${confirmResult.rows_imported} líneas pendientes publicadas · ` +
        `${confirmResult.rows_unresolved_material} sin material resuelto · ` +
        `${confirmResult.rows_without_delivery_date} sin fecha de entrega`,
    };
  } catch (error) {
    return {
      ...state,
      confirmResult: null,
      status: 'error',
      error: getErrorMessage(
        error,
        'No se pudo confirmar la importación de pedidos de compra.',
      ),
      resultMessage: null,
    };
  }
}
